/**
 * Anonymous message pool helpers (HORO-10524).
 *
 * The anonymous tier is `CHAT_MAX_MESSAGES_UNREGISTER` messages per
 * `ANON_MESSAGES_PERIOD_DAYS`. Until now the counter was a lifetime tally: it
 * only ever grew, so a visitor who spent their ten messages was out
 * permanently rather than until the window turned over.
 *
 * The window rolls on read rather than on a timer, mirroring
 * `ensureTarotRolled` on the backend. That ordering matters — the
 * limit is checked *before* a send, so a roll that only happened on write
 * would never fire for an exhausted visitor: the gate would refuse the
 * send, and the send was the only thing that would have reset them.
 *
 * Pure helpers — no React, no Redux — so `helper.js` and `authSlice` can
 * both use them without a circular import, the same reason
 * `tarotCredits.js` sits at this level.
 *
 * Scope note: this is client-side only, exactly as the limit it rolls is.
 * Clearing site data still hands an anonymous visitor a fresh pool; that
 * predates this change and needs server-side anonymous metering to fix.
 */
import {
    ANON_MESSAGES_PERIOD_DAYS,
    STORAGE_KEY_ANON_MESSAGES_EXP,
    STORAGE_KEY_CHATS_MESSAGES_COUNT,
    STORAGE_KEY_CHATS_MESSAGES_COUNT_LEGACY
} from '../const';
import StorageService from './StorageService';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * `ANON_MESSAGES_PERIOD_DAYS` from now, as Unix epoch milliseconds.
 *
 * A rolling window rather than a calendar month, because that is what a
 * registered free account gets: `makeUsersFree` advances `plan.validUntil` by
 * FREE_PLAN_PERIOD from whenever the sweep runs. Two different meanings of
 * "month" either side of registration would be a support answer that changes
 * depending on whether someone has an account.
 *
 * Epoch ms rather than an ISO string: it is what `Date.now()` already returns,
 * so the comparison below needs no parsing, and it reads as an opaque number to
 * anyone browsing localStorage. Plain millisecond arithmetic also sidesteps
 * calendar-rollover and DST entirely.
 */
export const nextAnonMessagesExpiry = (from = new Date()) => from.getTime() + ANON_MESSAGES_PERIOD_DAYS * DAY_MS;

/**
 * Zero the pool if the stored boundary has passed, and return the boundary
 * now in force.
 *
 * A visitor carrying counters from before this shipped has no stored
 * boundary. They are seeded into a fresh window with their tally intact
 * rather than cleared — clearing would hand every existing anonymous user a
 * fresh ten on deploy day, which is a quota giveaway nobody asked for.
 */
export const ensureAnonMessagesRolled = () => {
    // Carry a pre-rename tally onto the current key before anything reads it.
    // Done first so the roll below decides against the real count, and so the
    // rename is not itself a refill for visitors already part-way through their
    // allowance. The old key goes either way, so it can't be re-adopted later.
    const legacyCounters = StorageService.get(STORAGE_KEY_CHATS_MESSAGES_COUNT_LEGACY);
    if (legacyCounters) {
        if (!StorageService.get(STORAGE_KEY_CHATS_MESSAGES_COUNT)) {
            StorageService.add(STORAGE_KEY_CHATS_MESSAGES_COUNT, legacyCounters);
        }
        StorageService.delete(STORAGE_KEY_CHATS_MESSAGES_COUNT_LEGACY);
    }

    // Anything non-numeric — a hand-edited value, a stale format — counts as no
    // boundary rather than as an expired one, so a tampered value can't be used
    // to force a refill.
    const expiresAt = Number(StorageService.get(STORAGE_KEY_ANON_MESSAGES_EXP));
    const hasBoundary = Number.isFinite(expiresAt) && expiresAt > 0;

    if (hasBoundary && Date.now() < expiresAt) return expiresAt;

    // Expired boundary → new window, so the tally goes. Missing or unusable
    // boundary → first sight of this client, so only the boundary is seeded.
    if (hasBoundary) StorageService.delete(STORAGE_KEY_CHATS_MESSAGES_COUNT);

    const next = nextAnonMessagesExpiry();
    StorageService.add(STORAGE_KEY_ANON_MESSAGES_EXP, next);
    return next;
};

/**
 * The per-chat counter map, rolled first. Every read of the anonymous pool
 * goes through here so no caller can act on an expired window.
 */
export const readAnonMessageCounters = () => {
    ensureAnonMessagesRolled();
    return StorageService.get(STORAGE_KEY_CHATS_MESSAGES_COUNT) || {};
};

/**
 * Drop the pool and its boundary together.
 *
 * Intentionally has no callers. Sign-in is the obvious place to reach for it —
 * metering does move to the user document there — but clearing at sign-in made
 * signing in and straight back out a free refill of the pre-login allowance.
 * The pool is a per-device periodic limit and is meant to outlive any single
 * session, so it is left in place instead; it is inert while authenticated
 * (only read on the anonymous branch of the send gate). Kept as the
 * counterpart to read/roll for tests and for a deliberate reset.
 */
export const clearAnonMessageCredits = () => {
    StorageService.delete(STORAGE_KEY_CHATS_MESSAGES_COUNT);
    StorageService.delete(STORAGE_KEY_ANON_MESSAGES_EXP);
    StorageService.delete(STORAGE_KEY_CHATS_MESSAGES_COUNT_LEGACY);
};
